import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Star, MessageCircle, Phone, CheckCircle2, Clock, Send, RefreshCw,
  Search, ExternalLink, Award, UserCheck, ChevronRight, Sparkles, Filter
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { telLink, waLink, fmtDate, initials } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const REVIEW_LINK = "https://g.page/r/CS4EpHefbhmvEBM/review";

export default function ReviewRequests() {
  const { user, settings } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [templateLang, setTemplateLang] = useState("hinglish"); // hinglish | hindi | english

  const googleReviewUrl = settings?.google_review_url || REVIEW_LINK;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/reviews");
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update Review Status
  const handleStatusChange = async (patientId, newStatus) => {
    try {
      await api.post(`/reviews/${patientId}/status`, { status: newStatus });
      toast.success(`Review status updated to ${newStatus.toUpperCase()}`);
      setItems((prev) =>
        prev.map((p) => (p.id === patientId ? { ...p, review_status: newStatus } : p))
      );
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Generate Multilingual Review Template
  const getReviewMessage = (patientName, sessionsCount) => {
    const pName = patientName || "Sir/Ma'am";
    const sess = sessionsCount || 4;

    if (templateLang === "hindi") {
      return `नमस्ते ${pName} जी! 🙏\n\nबीके स्पाइन केयर क्लिनिक में आपके ${sess}+ फिजियोथेरेपी सेशन पूरे हो चुके हैं। हमें पूरी आशा है कि आप अब अपने दर्द से बहुत बेहतर महसूस कर रहे हैं।\n\nकृपया 1 मिनट का समय निकालकर गूगल पर अपना अनुभव और 5-स्टार रेटिंग जरूर साझा करें:\n🔗 ${googleReviewUrl}\n\n⭐⭐⭐⭐⭐ आपका सहयोग और रिव्यू हमारे लिए अत्यंत मूल्यवान है। धन्यवाद!`;
    }
    if (templateLang === "english") {
      return `Dear ${pName}, greetings from BK Spine Care Physiotherapy Clinic! 🙏\n\nWe are delighted to see your progress across your ${sess}+ therapy sessions. We hope you are experiencing great relief!\n\nCould you please take 1 minute to share your 5-star review on Google?\n🔗 ${googleReviewUrl}\n\n⭐⭐⭐⭐⭐ Your feedback helps other patients find trusted spine and physiotherapy care. Thank you!`;
    }
    // Default: Hinglish
    return `Namaste ${pName} ji! 🙏\n\nBK Spine Care Physiotherapy Clinic me aapke ${sess}+ therapy sessions complete ho chuke hain. Hume umeed hai aapki tabiyat ab pehle se kaafi behtar hai.\n\nKripya 1 minute nikal kar Google par apna valuable 5-star review zaroor share karein:\n🔗 ${googleReviewUrl}\n\n⭐⭐⭐⭐⭐ Aapka feedback humare liye bahut mahatvapurna hai. Dhanyavaad!`;
  };

  // 1-Tap WhatsApp Request Handler
  const handleSendWhatsApp = (patient) => {
    const msg = getReviewMessage(patient.name, patient.total_present_days);
    const link = waLink(patient.whatsapp || patient.phone, msg);
    window.open(link, "_blank");

    // Automatically mark as requested if currently pending
    if (patient.review_status !== "done") {
      handleStatusChange(patient.id, "requested");
    }
  };

  // Counts
  const pendingCount = useMemo(() => items.filter((p) => p.review_status === "pending" || !p.review_status).length, [items]);
  const requestedCount = useMemo(() => items.filter((p) => p.review_status === "requested").length, [items]);
  const doneCount = useMemo(() => items.filter((p) => p.review_status === "done").length, [items]);

  // Filtered Patients
  const filtered = useMemo(() => {
    let list = items;
    if (statusFilter !== "all") {
      list = list.filter((p) => (p.review_status || "pending") === statusFilter);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          p.patient_code?.toLowerCase().includes(q) ||
          p.phone?.includes(q) ||
          p.locality?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [items, statusFilter, search]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Google Review Requests"
        subtitle="Request 5-star Google reviews from satisfied patients who have completed 4+ therapy sessions"
        testid="review-requests-page"
        action={
          <div className="flex items-center gap-2">
            <a href={googleReviewUrl} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="gap-1.5 bg-white text-bk-blue">
                <ExternalLink className="h-4 w-4" /> Open Google Review Link
              </Button>
            </a>
            <Button
              size="sm"
              onClick={loadData}
              variant="outline"
              className="gap-1.5 bg-white"
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      {/* Top 4 KPI Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card
          onClick={() => setStatusFilter("all")}
          className={`p-3.5 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "all" ? "ring-2 ring-bk-blue bg-blue-50/40 border-bk-blue" : "bg-white"
          }`}
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-bk-blue">{items.length}</p>
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Eligible Patients</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Attended 4+ sessions</p>
        </Card>

        <Card
          onClick={() => setStatusFilter("pending")}
          className={`p-3.5 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "pending" ? "ring-2 ring-amber-500 bg-amber-50/40 border-amber-500" : "bg-white"
          }`}
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-amber-600">{pendingCount}</p>
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Pending Request</p>
          <p className="text-[10px] text-slate-400 mt-0.5">Ready to send review link</p>
        </Card>

        <Card
          onClick={() => setStatusFilter("requested")}
          className={`p-3.5 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "requested" ? "ring-2 ring-indigo-500 bg-indigo-50/40 border-indigo-500" : "bg-white"
          }`}
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-indigo-600">{requestedCount}</p>
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Request Sent</p>
          <p className="text-[10px] text-slate-400 mt-0.5">WhatsApp link delivered</p>
        </Card>

        <Card
          onClick={() => setStatusFilter("done")}
          className={`p-3.5 text-center cursor-pointer transition-all hover:shadow-sm ${
            statusFilter === "done" ? "ring-2 ring-emerald-500 bg-emerald-50/40 border-emerald-500" : "bg-white"
          }`}
        >
          <p className="font-display font-extrabold text-2xl tabular-nums text-bk-green">{doneCount}</p>
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider mt-0.5">Reviews Received</p>
          <p className="text-[10px] text-emerald-600 font-medium mt-0.5">Completed 5-star reviews</p>
        </Card>
      </div>

      {/* Filter and Language Toolbar */}
      <Card className="p-3.5 bg-slate-50/90 border-slate-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Status Filter Dropdown */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 flex items-center gap-1">
              <Filter className="h-3.5 w-3.5" /> Filter:
            </span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-44 bg-white font-medium">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Eligible ({items.length})</SelectItem>
                <SelectItem value="pending">Pending Request ({pendingCount})</SelectItem>
                <SelectItem value="requested">Request Sent ({requestedCount})</SelectItem>
                <SelectItem value="done">Review Done ({doneCount})</SelectItem>
              </SelectContent>
            </Select>

            {/* Template Language Selector */}
            <div className="flex items-center gap-1.5 ml-2">
              <span className="text-xs font-semibold text-slate-500">Message Language:</span>
              <div className="flex bg-white rounded-md border p-0.5">
                <button
                  type="button"
                  onClick={() => setTemplateLang("hinglish")}
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    templateLang === "hinglish" ? "bg-bk-blue text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Hinglish
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateLang("hindi")}
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    templateLang === "hindi" ? "bg-bk-blue text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Hindi
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateLang("english")}
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    templateLang === "english" ? "bg-bk-blue text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  English
                </button>
              </div>
            </div>
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Search patient, phone, locality..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs bg-white"
            />
          </div>
        </div>
      </Card>

      {/* Patient Cards Grid */}
      {loading ? (
        <Loading label="Loading review eligible patients..." />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Star}
          title="No review requests found"
          subtitle="Patients will appear here automatically once they complete 4+ therapy sessions."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((pat) => {
            const isDone = pat.review_status === "done";
            const isRequested = pat.review_status === "requested";

            return (
              <Card
                key={pat.id}
                className="p-4 hover:shadow-md transition-all border-slate-200 flex flex-col justify-between bg-white relative overflow-hidden group"
              >
                {/* Top Accent bar */}
                <div
                  className={`absolute top-0 left-0 right-0 h-1 ${
                    isDone ? "bg-emerald-500" : isRequested ? "bg-indigo-500" : "bg-amber-500"
                  }`}
                />

                <div className="space-y-3 pt-1">
                  {/* Header: Avatar, Name, Code */}
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <Avatar
                        className="h-10 w-10 cursor-pointer ring-2 ring-slate-100 hover:ring-bk-blue transition-all shrink-0"
                        onClick={() => navigate(`/patients/${pat.id}`)}
                      >
                        <AvatarFallback className="bg-amber-50 text-amber-700 font-bold text-xs">
                          {initials(pat.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <button
                          onClick={() => navigate(`/patients/${pat.id}`)}
                          className="font-bold text-slate-900 text-sm hover:text-bk-blue text-left truncate block max-w-[170px] leading-tight"
                        >
                          {pat.name}
                        </button>
                        <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                          {pat.patient_code}
                        </p>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="shrink-0">
                      {isDone ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-2 py-0.5">
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Review Done
                        </Badge>
                      ) : isRequested ? (
                        <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 text-[10px] px-2 py-0.5">
                          <Send className="h-3 w-3 mr-1" /> Request Sent
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-2 py-0.5">
                          <Clock className="h-3 w-3 mr-1" /> Pending Request
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Sessions & Clinical Info */}
                  <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50/80 rounded-lg border border-slate-100 text-xs">
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-slate-400">Attended Sessions</p>
                      <p className="font-bold text-slate-800 flex items-center gap-1 mt-0.5">
                        <Award className="h-3.5 w-3.5 text-amber-500" /> {pat.total_present_days} Sessions
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-slate-400">Last Visit Date</p>
                      <p className="font-semibold text-slate-700 mt-0.5">
                        {pat.last_visit_date ? fmtDate(pat.last_visit_date) : "Recent"}
                      </p>
                    </div>
                  </div>

                  {pat.problem && (
                    <p className="text-xs text-slate-600 line-clamp-1 italic bg-amber-50/50 px-2 py-1 rounded border border-amber-100/60">
                      Condition: {pat.problem}
                    </p>
                  )}
                </div>

                {/* Card Actions */}
                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-1.5">
                  {/* Contact shortcuts */}
                  <div className="flex items-center gap-1">
                    <a href={telLink(pat.phone)} title="Call Patient">
                      <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-bk-blue">
                        <Phone className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  </div>

                  {/* WhatsApp Review Button & Review Done Action */}
                  <div className="flex items-center gap-1.5">
                    <Button
                      size="sm"
                      onClick={() => handleSendWhatsApp(pat)}
                      className="h-7 px-2 text-[11px] bg-bk-green hover:bg-emerald-700 text-white gap-1"
                      title="Send WhatsApp Review Request"
                    >
                      <MessageCircle className="h-3 w-3" />
                      {isRequested ? "Resend Review Link" : "Send Review Link"}
                    </Button>

                    {!isDone ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusChange(pat.id, "done")}
                        className="h-7 px-2 text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        title="Mark Review Received"
                      >
                        <CheckCircle2 className="h-3 w-3 mr-0.5" /> Review Done
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStatusChange(pat.id, "pending")}
                        className="h-7 px-1.5 text-[10px] text-slate-400 hover:text-slate-600"
                        title="Reset to Pending"
                      >
                        Reset
                      </Button>
                    )}

                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/patients/${pat.id}`)}
                      className="h-7 w-7 p-0 text-slate-400 hover:text-bk-blue"
                      title="View Patient Profile"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
